import { formatDate, formatDateTime, formatINR } from "@templeos/shared";
import type { ButtonHTMLAttributes, ReactNode } from "react";

export type Tone = "neutral" | "success" | "warning" | "danger" | "info" | "accent";
const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "success";
  size?: "sm" | "md" | "lg";
  block?: boolean;
  loading?: boolean;
}

export function Button({ variant = "secondary", size = "md", block, loading, className, children, disabled, type = "button", ...rest }: ButtonProps) {
  return (
    <button type={type} className={cx("tos-btn", `tos-btn-${variant}`, size !== "md" && `tos-btn-${size}`, block && "tos-btn-block", className)}
      disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading && <span className="tos-spinner" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function Card({ title, actions, children, className, bodyClassName }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string }) {
  return (
    <section className={cx("tos-card", className)}>
      {(title || actions) && (
        <header className="tos-card-head">
          {title && <h2 className="tos-card-title">{title}</h2>}
          <div className="tos-spacer" />
          {actions}
        </header>
      )}
      <div className={cx("tos-card-body", bodyClassName)}>{children}</div>
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="tos-page-head">
      <div>
        <h1 className="tos-page-title">{title}</h1>
        {subtitle && <p className="tos-page-sub">{subtitle}</p>}
      </div>
      <div className="tos-spacer" />
      {actions && <div className="tos-row tos-no-print">{actions}</div>}
    </div>
  );
}

export function Badge({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={cx("tos-badge", tone !== "neutral" && `tone-${tone}`)}>{children}</span>;
}

export function StatCard({ label, value, hint, tone }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: Tone }) {
  return (
    <div className={cx("tos-card tos-stat", tone && `tone-${tone}`)}>
      <div className="tos-stat-label">{label}</div>
      <div className="tos-stat-value">{value}</div>
      {hint && <div className="tos-stat-hint">{hint}</div>}
    </div>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return <span role="status" className="tos-row"><span className="tos-spinner" aria-hidden="true" /><span className="tos-muted">{label}…</span></span>;
}

export function EmptyState({ title, children, action }: { title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="tos-empty">
      <div className="tos-empty-title">{title}</div>
      {children && <div>{children}</div>}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

export function Alert({ tone = "info", children }: { tone?: Exclude<Tone, "neutral" | "accent">; children: ReactNode }) {
  return <div role={tone === "danger" ? "alert" : "status"} className={cx("tos-alert", `tone-${tone}`)}>{children}</div>;
}

/** Money display from integer paise. */
export function Money({ paise, decimals = true, className }: { paise: number | null | undefined; decimals?: boolean; className?: string }) {
  if (paise === null || paise === undefined) return <span className={cx("tos-num tos-muted", className)}>—</span>;
  return <span className={cx("tos-num", paise < 0 && "tos-neg", className)}>{formatINR(paise, { decimals })}</span>;
}

export function DateText({ value, time }: { value: string | Date | null | undefined; time?: boolean }) {
  if (!value) return <span className="tos-muted">—</span>;
  return <span className="tos-num">{time ? formatDateTime(value) : formatDate(value)}</span>;
}
